// src/errors.ts
var TestnetError = class extends Error {
  code;
  constructor(message, code) {
    super(message);
    this.name = "TestnetError";
    this.code = code;
  }
};
var AnvilStartupError = class extends TestnetError {
  constructor(message) {
    super(message, "ANVIL_STARTUP");
    this.name = "AnvilStartupError";
  }
};
var AnvilTimeoutError = class extends TestnetError {
  constructor(message) {
    super(message, "ANVIL_TIMEOUT");
    this.name = "AnvilTimeoutError";
  }
};
var PortConflictError = class extends TestnetError {
  constructor(message) {
    super(message, "PORT_CONFLICT");
    this.name = "PortConflictError";
  }
};
var SnapshotNotFoundError = class extends TestnetError {
  constructor(message) {
    super(message, "SNAPSHOT_NOT_FOUND");
    this.name = "SnapshotNotFoundError";
  }
};
var AccountError = class extends TestnetError {
  constructor(message) {
    super(message, "ACCOUNT_ERROR");
    this.name = "AccountError";
  }
};
var DeploymentError = class extends TestnetError {
  constructor(message) {
    super(message, "DEPLOYMENT_ERROR");
    this.name = "DeploymentError";
  }
};
var HealthCheckError = class extends TestnetError {
  constructor(message) {
    super(message, "HEALTH_CHECK_FAILED");
    this.name = "HealthCheckError";
  }
};

// src/accounts.ts
import { toHex } from "viem";
import { mnemonicToAccount } from "viem/accounts";
var ANVIL_MNEMONIC = "test test test test test test test test test test test junk";
var ANVIL_DEPLOYER_ADDRESS = "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266";
var ANVIL_ACCOUNTS = [
  {
    address: ANVIL_DEPLOYER_ADDRESS,
    privateKey: "0xac0974bec39a17e36ba4a6b4d238ff944bacb478cbed5efcae784d7bf4f2ff80",
    label: "deployer"
  },
  {
    address: "0x70997970C51812dc3A010C7d01b50e0d17dc79C8",
    privateKey: "0x59c6995e998f97a5a0044966f0945389dc9e86dae88c7a8412f4603b6b78690d",
    label: "alice"
  },
  {
    address: "0x3C44CdDdB6a900fa2b585dd299e03d12FA4293BC",
    privateKey: "0x5de4111afa1a4b94908f83103eb1f1706367c2e68ca870fc3fb9a804cdab365a",
    label: "bob"
  },
  {
    address: "0x90F79bf6EB2c4f870365E785982E1f101E93b906",
    privateKey: "0x7c852118294e51e653712a81e05800f419141751be58f605c371e15141b007a6",
    label: "carol"
  },
  {
    address: "0x15d34AAf54267DB7D7c367839AAf71A00a2C6A65",
    privateKey: "0x47e179ec197488593b187f80a00eb0da91f1b9d0b13f8733639f19c30a34926a",
    label: "dave"
  },
  {
    address: "0x9965507D1a55bcC2695C58ba16FB37d819B0A4dc",
    privateKey: "0x8b3a350cf5c34c9194ca85829a2df0ec3153be0318b5e2d3348e872092edffba",
    label: "eve"
  },
  {
    address: "0x976EA74026E726554dB657fA54763abd0C3a0aa9",
    privateKey: "0x92db14e403b83dfe3df233f83dfa3a0d7096f21ca9b0d6d6b8d88b2b4ec1564e",
    label: "frank"
  },
  {
    address: "0x14dC79964da2C08b23698B3D3cc7Ca32193d9955",
    privateKey: "0x4bbbf85ce3377467afe5d46f804f221813b2bb87f24d81f60f1fcdbf7cbf4356",
    label: "grace"
  },
  {
    address: "0x23618e81E3f5cdF7f54C3d65f7FBc0aBf5B21E8f",
    privateKey: "0xdbda1821b80551c9d65939329250298aa3472ba22feea921c0cf5d620ea67b97",
    label: "heidi"
  },
  {
    address: "0xa0Ee7A142d267C1f36714E4a8F75612F20a79720",
    privateKey: "0x2a871d0798f97d79848a013d4936a73bf4cc922c825d33c1cf7073dff6d409c6",
    label: "ivan"
  }
];
function getAccount(indexOrLabel) {
  if (typeof indexOrLabel === "number") {
    const account2 = ANVIL_ACCOUNTS[indexOrLabel];
    if (!account2) {
      throw new Error(
        `Account index ${String(indexOrLabel)} out of range (0-9)`
      );
    }
    return account2;
  }
  const account = ANVIL_ACCOUNTS.find((a) => a.label === indexOrLabel);
  if (!account) {
    throw new Error(`Account with label "${indexOrLabel}" not found`);
  }
  return account;
}
function generateAccounts(count = 20, startIndex = 10) {
  const accounts = [];
  for (let i = 0; i < count; i++) {
    const idx = startIndex + i;
    const hdAccount = mnemonicToAccount(ANVIL_MNEMONIC, {
      addressIndex: idx
    });
    const rawKey = hdAccount.getHdKey().privateKey;
    if (!rawKey) {
      throw new Error(`Failed to derive private key for index ${String(idx)}`);
    }
    const privateKey = toHex(rawKey);
    accounts.push({
      address: hdAccount.address,
      privateKey,
      label: `account-${String(idx)}`
    });
  }
  return accounts;
}
async function fundAccount(client, address, balanceWei) {
  const hexBalance = "0x" + balanceWei.toString(16);
  await client.request({
    method: "anvil_setBalance",
    params: [address, hexBalance]
  });
}
async function drainAccount(client, address) {
  await fundAccount(client, address, 0n);
}
function derivationPath(index) {
  return `m/44'/60'/0'/0/${String(index)}`;
}

// src/anvil.ts
import { execFile, spawn } from "child_process";
import { EventEmitter } from "events";
import { createPublicClient, http } from "viem";
function getAnvilVersion(anvilBinary) {
  return new Promise((resolve) => {
    execFile(anvilBinary ?? "anvil", ["--version"], (err, stdout) => {
      if (err) {
        resolve(null);
        return;
      }
      const match = /(\d+)\.(\d+)\.(\d+)/.exec(stdout);
      if (!match) {
        resolve(null);
        return;
      }
      resolve({
        major: parseInt(match[1] ?? "0", 10),
        minor: parseInt(match[2] ?? "0", 10),
        patch: parseInt(match[3] ?? "0", 10)
      });
    });
  });
}
var AnvilManager = class extends EventEmitter {
  #options;
  #process = null;
  #instance = null;
  #started = false;
  #instances = /* @__PURE__ */ new Map();
  constructor(options = {}) {
    super();
    const stateFile = options.stateFile;
    this.#options = {
      port: options.port ?? 0,
      chainId: options.chainId ?? 31337,
      accounts: options.accounts ?? 20,
      balance: options.balance ?? 1e4,
      blockTime: options.blockTime ?? 0,
      forkUrl: options.forkUrl ?? "",
      forkBlockNumber: options.forkBlockNumber ?? 0,
      gasLimit: options.gasLimit ?? 3e7,
      gasPrice: options.gasPrice ?? 0,
      baseFee: options.baseFee ?? 0,
      hardfork: options.hardfork ?? "cancun",
      autoImpersonate: options.autoImpersonate ?? false,
      silent: options.silent ?? true,
      startupTimeout: options.startupTimeout ?? 3e4,
      onLog: options.onLog ?? (() => void 0),
      anvilBinary: options.anvilBinary ?? options.anvilBin ?? "anvil",
      extraArgs: options.extraArgs ?? [],
      codeSizeLimit: options.codeSizeLimit ?? 3e4,
      stepsTracing: options.stepsTracing ?? true,
      computeUnitsPerSecond: options.computeUnitsPerSecond ?? 0,
      dumpState: options.dumpState ?? stateFile ?? "",
      loadState: options.loadState ?? stateFile ?? process.env.DEVENV_LOAD_STATE ?? "",
      stateInterval: options.stateInterval ?? 0,
      cachePath: options.cachePath ?? ""
    };
  }
  /** Build CLI arguments from options. */
  buildArgs(opts) {
    const args = [];
    const o = opts ?? this.#options;
    args.push("--port", String(o.port));
    args.push("--chain-id", String(o.chainId));
    args.push("--accounts", String(o.accounts));
    args.push("--balance", String(o.balance));
    if (o.blockTime > 0) args.push("--block-time", String(o.blockTime));
    if (o.forkUrl) {
      args.push("--fork-url", o.forkUrl);
      if (o.forkBlockNumber > 0) {
        args.push("--fork-block-number", String(o.forkBlockNumber));
      }
    }
    args.push("--gas-limit", String(o.gasLimit));
    args.push("--gas-price", String(o.gasPrice));
    args.push("--base-fee", String(o.baseFee));
    args.push("--hardfork", o.hardfork);
    if (o.autoImpersonate) args.push("--auto-impersonate");
    if (o.codeSizeLimit > 0)
      args.push("--code-size-limit", String(o.codeSizeLimit));
    if (o.stepsTracing) args.push("--steps-tracing");
    if (o.computeUnitsPerSecond > 0)
      args.push("--compute-units-per-second", String(o.computeUnitsPerSecond));
    if (o.dumpState) args.push("--dump-state", o.dumpState);
    if (o.loadState) args.push("--load-state", o.loadState);
    if (o.stateInterval > 0)
      args.push("--state-interval", String(o.stateInterval));
    if (o.cachePath) args.push("--cache-path", o.cachePath);
    args.push(...o.extraArgs);
    return args;
  }
  /** Start Anvil (single-instance, backward compatible). Resolves when ready. */
  async start() {
    if (this.#started) {
      throw new AnvilStartupError("Anvil is already running");
    }
    const instance = await this.#spawn(this.#options);
    this.#process = instance.process;
    this.#instance = instance.instance;
    this.#started = true;
    this.emit("started", this.#instance);
    return this.#instance;
  }
  /** Stop the Anvil process (single-instance). */
  stop() {
    if (!this.#started || !this.#process) return;
    this.#kill(this.#process);
    this.#started = false;
    this.#instance = null;
    this.emit("stopped");
  }
  /** Start a named instance. */
  async startInstance(id, options) {
    if (this.#instances.has(id)) {
      throw new AnvilStartupError(`Instance "${id}" is already running`);
    }
    const merged = this.#mergeOptions(options);
    const result = await this.#spawn(merged);
    this.#instances.set(id, {
      instance: result.instance,
      process: result.process,
      options: merged
    });
    this.emit("started", result.instance);
    return result.instance;
  }
  /** Stop a named instance. */
  stopInstance(id) {
    const managed = this.#instances.get(id);
    if (!managed) return;
    this.#kill(managed.process);
    this.#instances.delete(id);
    this.emit("stopped", id);
  }
  /** Restart a named instance. */
  async restartInstance(id) {
    const managed = this.#instances.get(id);
    if (!managed) {
      throw new AnvilStartupError(`Instance "${id}" is not running`);
    }
    const opts = managed.options;
    this.#kill(managed.process);
    this.#instances.delete(id);
    const result = await this.#spawn(opts);
    this.#instances.set(id, {
      instance: result.instance,
      process: result.process,
      options: opts
    });
    this.emit("started", result.instance);
    return result.instance;
  }
  /** List all named instance IDs. */
  listInstances() {
    return Array.from(this.#instances.keys());
  }
  /** Get a named instance by ID. */
  getInstance(id) {
    return this.#instances.get(id)?.instance;
  }
  /** Get the current instance info (null if not running). */
  get instance() {
    return this.#instance;
  }
  /** Whether Anvil is currently running (single-instance). */
  get running() {
    return this.#started;
  }
  /** The port Anvil is running on. */
  get port() {
    return this.#instance?.port ?? 0;
  }
  /** The RPC URL. */
  get rpcUrl() {
    return this.#instance?.rpcUrl ?? "";
  }
  #mergeOptions(overrides) {
    if (!overrides) return { ...this.#options };
    const stateFile = overrides.stateFile;
    return {
      ...this.#options,
      ...overrides,
      anvilBinary: overrides.anvilBinary ?? overrides.anvilBin ?? this.#options.anvilBinary,
      dumpState: overrides.dumpState ?? stateFile ?? this.#options.dumpState,
      loadState: overrides.loadState ?? stateFile ?? this.#options.loadState
    };
  }
  async #spawn(opts) {
    const port = opts.port === 0 ? await (await import("get-port")).default() : opts.port;
    const resolvedOpts = { ...opts, port };
    const args = this.buildArgs(resolvedOpts);
    return new Promise(
      (resolve, reject) => {
        let stderrBuf = "";
        const timeout = setTimeout(() => {
          this.#kill(proc);
          const detail = stderrBuf.trim() ? `
stderr: ${stderrBuf.trim()}` : "";
          reject(
            new AnvilTimeoutError(
              `Anvil failed to start within ${String(resolvedOpts.startupTimeout)}ms${detail}`
            )
          );
        }, resolvedOpts.startupTimeout);
        let proc;
        try {
          proc = spawn(resolvedOpts.anvilBinary, args, {
            stdio: ["ignore", "pipe", "pipe"]
          });
        } catch (err) {
          clearTimeout(timeout);
          reject(
            new AnvilStartupError(
              `Failed to spawn anvil: ${err instanceof Error ? err.message : String(err)}`
            )
          );
          return;
        }
        proc.on("error", (err) => {
          clearTimeout(timeout);
          if (err.message.includes("ENOENT")) {
            reject(
              new AnvilStartupError(
                "Anvil binary not found. Install Foundry: https://getfoundry.sh"
              )
            );
          } else {
            reject(
              new AnvilStartupError(`Anvil process error: ${err.message}`)
            );
          }
        });
        proc.stderr?.on("data", (chunk) => {
          const text = chunk.toString();
          stderrBuf += text;
          resolvedOpts.onLog("stderr", text);
          if (stderrBuf.includes("Address already in use") || stderrBuf.includes("EADDRINUSE")) {
            clearTimeout(timeout);
            reject(
              new PortConflictError(`Port ${String(port)} is already in use`)
            );
          }
        });
        let resolved = false;
        proc.stdout?.on("data", (chunk) => {
          const output = chunk.toString();
          resolvedOpts.onLog("stdout", output);
          const listenMatch = /Listening on [\d.]+:(\d+)/.exec(output);
          if (listenMatch && !resolved) {
            resolved = true;
            clearTimeout(timeout);
            const actualPort = parseInt(listenMatch[1] ?? "0", 10);
            const rpcUrl = `http://127.0.0.1:${String(actualPort)}`;
            const client = createPublicClient({
              transport: http(rpcUrl)
            });
            const accounts = generateAccounts(resolvedOpts.accounts, 0);
            let stopped = false;
            const instance = {
              port: actualPort,
              chainId: resolvedOpts.chainId,
              rpcUrl,
              wsUrl: `ws://127.0.0.1:${String(actualPort)}`,
              pid: proc.pid ?? 0,
              client,
              accounts,
              process: proc,
              stop: () => {
                if (!stopped) {
                  stopped = true;
                  this.#kill(proc);
                }
              },
              isRunning: () => !stopped && !proc.killed
            };
            resolve({ instance, process: proc });
          }
        });
        proc.on("exit", (code) => {
          clearTimeout(timeout);
          if (!resolved) {
            reject(
              new AnvilStartupError(
                `Anvil exited with code ${String(code)} before becoming ready. stderr: ${stderrBuf}`
              )
            );
          }
        });
      }
    );
  }
  #kill(proc) {
    if (!proc.killed) {
      proc.kill("SIGTERM");
      const p = proc;
      setTimeout(() => {
        if (!p.killed) p.kill("SIGKILL");
      }, 2e3);
    }
  }
};

// src/time-travel.ts
var MULTIPLIERS = {
  s: 1,
  m: 60,
  h: 3600,
  d: 86400,
  w: 604800
};
function parseDuration(input) {
  const match = /^(\d+)(s|m|h|d|w)$/.exec(input);
  if (!match) {
    throw new Error(
      `Invalid duration: "${input}". Use format like "30m", "2h", "1d"`
    );
  }
  const value = parseInt(match[1] ?? "", 10);
  const unit = match[2];
  return value * MULTIPLIERS[unit];
}
function resolveDuration(duration) {
  if (typeof duration === "number") return duration;
  return parseDuration(duration);
}
async function timeTravel(client, seconds) {
  const secs = resolveDuration(seconds);
  await client.request({
    method: "evm_increaseTime",
    params: [secs]
  });
  await client.request({ method: "evm_mine", params: [] });
}
async function setTimestamp(client, timestamp) {
  await client.request({
    method: "evm_setNextBlockTimestamp",
    params: [timestamp]
  });
  await client.request({ method: "evm_mine", params: [] });
}
async function setNextBlockTimestamp(client, timestamp) {
  await client.request({
    method: "evm_setNextBlockTimestamp",
    params: [timestamp]
  });
}
async function mineBlocks(client, count, opts) {
  const interval = opts?.interval ?? 1;
  await client.request({
    method: "anvil_mine",
    params: [count, interval]
  });
}
async function getBlockTimestamp(client) {
  const block = await client.request({
    method: "eth_getBlockByNumber",
    params: ["latest", false]
  });
  return parseInt(block.timestamp, 16);
}

// src/contracts/MockERC20.ts
var MOCK_ERC20_ABI = [
  // constructor(string name, string symbol, uint8 decimals)
  {
    type: "constructor",
    inputs: [
      { name: "name_", type: "string" },
      { name: "symbol_", type: "string" },
      { name: "decimals_", type: "uint8" }
    ],
    stateMutability: "nonpayable"
  },
  // Standard ERC20 functions
  {
    type: "function",
    name: "name",
    inputs: [],
    outputs: [{ type: "string" }],
    stateMutability: "view"
  },
  {
    type: "function",
    name: "symbol",
    inputs: [],
    outputs: [{ type: "string" }],
    stateMutability: "view"
  },
  {
    type: "function",
    name: "decimals",
    inputs: [],
    outputs: [{ type: "uint8" }],
    stateMutability: "view"
  },
  {
    type: "function",
    name: "totalSupply",
    inputs: [],
    outputs: [{ type: "uint256" }],
    stateMutability: "view"
  },
  {
    type: "function",
    name: "balanceOf",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
    stateMutability: "view"
  },
  {
    type: "function",
    name: "transfer",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" }
    ],
    outputs: [{ type: "bool" }],
    stateMutability: "nonpayable"
  },
  {
    type: "function",
    name: "allowance",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" }
    ],
    outputs: [{ type: "uint256" }],
    stateMutability: "view"
  },
  {
    type: "function",
    name: "approve",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" }
    ],
    outputs: [{ type: "bool" }],
    stateMutability: "nonpayable"
  },
  {
    type: "function",
    name: "transferFrom",
    inputs: [
      { name: "from", type: "address" },
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" }
    ],
    outputs: [{ type: "bool" }],
    stateMutability: "nonpayable"
  },
  // Mint (anyone can call — test only)
  {
    type: "function",
    name: "mint",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" }
    ],
    outputs: [],
    stateMutability: "nonpayable"
  },
  // Burn
  {
    type: "function",
    name: "burn",
    inputs: [{ name: "amount", type: "uint256" }],
    outputs: [],
    stateMutability: "nonpayable"
  },
  // Events
  {
    type: "event",
    name: "Transfer",
    inputs: [
      { name: "from", type: "address", indexed: true },
      { name: "to", type: "address", indexed: true },
      { name: "value", type: "uint256", indexed: false }
    ]
  },
  {
    type: "event",
    name: "Approval",
    inputs: [
      { name: "owner", type: "address", indexed: true },
      { name: "spender", type: "address", indexed: true },
      { name: "value", type: "uint256", indexed: false }
    ]
  }
];
var MOCK_ERC20_BYTECODE = "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";

// src/tokens.ts
import {
  encodeAbiParameters,
  encodeFunctionData,
  keccak256,
  numberToHex,
  pad,
  toHex as toHex2
} from "viem";
async function sendTransactionSync(client, tx) {
  const params = {
    from: tx.from,
    to: tx.to,
    ...tx.data ? { data: tx.data } : {},
    ...tx.value ? { value: numberToHex(tx.value) } : {}
  };
  try {
    const result = await client.request({
      method: "eth_sendTransactionSync",
      params: [params]
    });
    if (result !== null && typeof result === "object" && "hash" in result && "receipt" in result) {
      return result;
    }
    const hash = typeof result === "string" ? result : result.hash;
    const receipt = await client.request({
      method: "eth_getTransactionReceipt",
      params: [hash]
    });
    return { hash, receipt };
  } catch {
    const hash = await client.request({
      method: "eth_sendTransaction",
      params: [params]
    });
    const receipt = await client.request({
      method: "eth_getTransactionReceipt",
      params: [hash]
    });
    return { hash, receipt };
  }
}
async function getERC20Balance(client, token, account) {
  const data = encodeFunctionData({
    abi: MOCK_ERC20_ABI,
    functionName: "balanceOf",
    args: [account]
  });
  const result = await client.request({
    method: "eth_call",
    params: [{ to: token, data }, "latest"]
  });
  if (result === "0x" || result === "0x0") return 0n;
  return BigInt(result);
}
async function deployERC20(client, deployerOrOptions, maybeOptions) {
  let deployer;
  let options;
  if (typeof deployerOrOptions === "string" && maybeOptions) {
    deployer = deployerOrOptions;
    options = maybeOptions;
  } else if (typeof deployerOrOptions === "object") {
    options = deployerOrOptions;
    deployer = options.deployer ?? ANVIL_DEPLOYER_ADDRESS;
  } else {
    deployer = deployerOrOptions;
    options = maybeOptions ?? { name: "", symbol: "" };
  }
  const decimals = options.decimals ?? 18;
  if (MOCK_ERC20_BYTECODE === "0x" || MOCK_ERC20_BYTECODE.length < 10) {
    throw new DeploymentError(
      "MockERC20 bytecode not available. Run forge build first."
    );
  }
  const constructorArgs = encodeAbiParameters(
    [
      { type: "string", name: "name_" },
      { type: "string", name: "symbol_" },
      { type: "uint8", name: "decimals_" }
    ],
    [options.name, options.symbol, decimals]
  );
  const deployData = MOCK_ERC20_BYTECODE + constructorArgs.slice(2);
  const txHash = await client.request({
    method: "eth_sendTransaction",
    params: [
      {
        from: deployer,
        data: deployData
      }
    ]
  });
  const receipt = await client.request({
    method: "eth_getTransactionReceipt",
    params: [txHash]
  });
  if (!receipt.contractAddress) {
    throw new DeploymentError(
      `Deployment transaction ${txHash} did not create a contract`
    );
  }
  return {
    address: receipt.contractAddress,
    name: options.name,
    symbol: options.symbol,
    decimals
  };
}
async function mintERC20(client, token, to, amount, from) {
  const sender = from ?? ANVIL_DEPLOYER_ADDRESS;
  const data = encodeFunctionData({
    abi: MOCK_ERC20_ABI,
    functionName: "mint",
    args: [to, amount]
  });
  await client.request({
    method: "eth_sendTransaction",
    params: [
      {
        from: sender,
        to: token,
        data
      }
    ]
  });
}
async function setERC20Balance(client, token, account, balance, mappingSlot) {
  try {
    await client.request({
      method: "anvil_dealERC20",
      params: [token, account, numberToHex(balance)]
    });
    return;
  } catch {
  }
  const slot = mappingSlot ?? 0;
  const storageKey = keccak256(
    encodeAbiParameters(
      [
        { type: "address", name: "key" },
        { type: "uint256", name: "slot" }
      ],
      [account, BigInt(slot)]
    )
  );
  const paddedBalance = pad(toHex2(balance), { size: 32 });
  await client.request({
    method: "anvil_setStorageAt",
    params: [token, storageKey, paddedBalance]
  });
}
async function addEthBalance(client, address, amountWei) {
  try {
    await client.request({
      method: "anvil_addBalance",
      params: [address, numberToHex(amountWei)]
    });
    return;
  } catch {
  }
  const currentHex = await client.request({
    method: "eth_getBalance",
    params: [address, "latest"]
  });
  const current = BigInt(currentHex);
  const newBalance = current + amountWei;
  const hexBalance = numberToHex(newBalance);
  await client.request({
    method: "anvil_setBalance",
    params: [address, hexBalance]
  });
}
async function setERC20Allowance(client, token, owner, spender, amount, mappingSlot) {
  try {
    await client.request({
      method: "anvil_setERC20Allowance",
      params: [token, owner, spender, numberToHex(amount)]
    });
    return;
  } catch {
  }
  const slot = mappingSlot ?? 1;
  const intermediateSlot = keccak256(
    encodeAbiParameters(
      [
        { type: "address", name: "key" },
        { type: "uint256", name: "slot" }
      ],
      [owner, BigInt(slot)]
    )
  );
  const storageKey = keccak256(
    encodeAbiParameters(
      [
        { type: "address", name: "key" },
        { type: "bytes32", name: "slot" }
      ],
      [spender, intermediateSlot]
    )
  );
  const paddedAmount = pad(toHex2(amount), { size: 32 });
  await client.request({
    method: "anvil_setStorageAt",
    params: [token, storageKey, paddedAmount]
  });
}

// src/utils/bigint-json.ts
function bigintReplacer(_key, value) {
  if (typeof value === "bigint") {
    return value.toString() + "n";
  }
  return value;
}
function bigintReviver(_key, value) {
  if (typeof value === "string" && /^\d+n$/.test(value)) {
    return BigInt(value.slice(0, -1));
  }
  return value;
}
function stringifyWithBigInt(value, space) {
  return JSON.stringify(value, bigintReplacer, space);
}
function parseWithBigInt(text) {
  return JSON.parse(text, bigintReviver);
}

// src/snapshots.ts
import { readFileSync, writeFileSync } from "fs";
var DEFAULT_SNAPSHOT_PATH = ".testnet-snapshots.json";
var SnapshotStore = class {
  #snapshots = /* @__PURE__ */ new Map();
  #client;
  #persistPath;
  constructor(client, persistPath) {
    this.#client = client;
    this.#persistPath = persistPath;
    if (persistPath) {
      this.loadFromFile(persistPath);
    }
  }
  /** Take a named snapshot of the current chain state. */
  async take(name, description) {
    const id = await this.#client.request({
      method: "evm_snapshot",
      params: []
    });
    const blockHex = await this.#client.request({
      method: "eth_blockNumber",
      params: []
    });
    let blockTimestamp = 0;
    try {
      const block = await this.#client.request({
        method: "eth_getBlockByNumber",
        params: [blockHex, false]
      });
      if (block?.timestamp) {
        blockTimestamp = parseInt(block.timestamp, 16);
      }
    } catch {
    }
    const now = Date.now();
    const snapshot = {
      id,
      name,
      createdAt: now,
      createdAtIso: new Date(now).toISOString(),
      timestamp: blockTimestamp,
      blockNumber: BigInt(blockHex),
      description
    };
    this.#snapshots.set(name, snapshot);
    this.#persist();
    return snapshot;
  }
  /**
   * Revert to a named snapshot.
   *
   * After a successful revert, automatically re-takes a new snapshot
   * with the same name so it can be reused (e.g. in beforeEach loops).
   * Anvil consumes snapshots on revert, so without re-taking the
   * snapshot would be gone.
   *
   * @throws {SnapshotNotFoundError} If the snapshot name is not found or revert fails.
   */
  async revert(name) {
    const snapshot = this.#snapshots.get(name);
    if (!snapshot) {
      const available = this.list().map((s) => s.name).join(", ") || "none";
      throw new SnapshotNotFoundError(
        `Snapshot "${name}" not found. Available: ${available}`
      );
    }
    const success = await this.#client.request({
      method: "evm_revert",
      params: [snapshot.id]
    });
    if (!success) {
      this.#snapshots.delete(name);
      this.#persist();
      throw new SnapshotNotFoundError(
        `Failed to revert to snapshot "${name}" (id: ${snapshot.id})`
      );
    }
    const newId = await this.#client.request({
      method: "evm_snapshot",
      params: []
    });
    this.#snapshots.set(name, {
      ...snapshot,
      id: newId
    });
    this.#persist();
  }
  /** List all stored snapshots. */
  list() {
    return Array.from(this.#snapshots.values());
  }
  /** List just the snapshot names. */
  listNames() {
    return Array.from(this.#snapshots.keys());
  }
  /** Check if a snapshot with the given name exists. */
  has(name) {
    return this.#snapshots.has(name);
  }
  /** Remove a snapshot from the store without reverting chain state. */
  delete(name) {
    const result = this.#snapshots.delete(name);
    if (result) this.#persist();
    return result;
  }
  /** Remove all snapshots from the store. */
  clear() {
    this.#snapshots.clear();
    this.#persist();
  }
  /** Get a snapshot by name, or `undefined` if not found. */
  get(name) {
    return this.#snapshots.get(name);
  }
  /** Number of stored snapshots. */
  get size() {
    return this.#snapshots.size;
  }
  /** Save snapshots to the configured persistence file. */
  saveToFile(path) {
    const filePath = path ?? this.#persistPath;
    if (!filePath) return;
    const data = Array.from(this.#snapshots.values());
    writeFileSync(filePath, JSON.stringify(data, bigintReplacer, 2), "utf-8");
  }
  /** Load snapshots from a persistence file (replaces current store). */
  loadFromFile(path) {
    const filePath = path ?? this.#persistPath;
    if (!filePath) return;
    try {
      const raw = readFileSync(filePath, "utf-8");
      const data = JSON.parse(raw, bigintReviver);
      this.#snapshots.clear();
      for (const snap of data) {
        this.#snapshots.set(snap.name, snap);
      }
    } catch {
    }
  }
  #persist() {
    if (this.#persistPath) {
      this.saveToFile();
    }
  }
};

// src/health.ts
import { createPublicClient as createPublicClient2, http as http2 } from "viem";
var TRANSIENT_STORAGE_PROBE_BYTECODE = "0x60426000605d6000605c50";
async function runCheck(name, fn) {
  const start = Date.now();
  try {
    const message = await fn();
    return {
      name,
      passed: true,
      status: "pass",
      message,
      durationMs: Date.now() - start
    };
  } catch (err) {
    return {
      name,
      passed: false,
      status: "fail",
      message: err instanceof Error ? err.message : String(err),
      durationMs: Date.now() - start
    };
  }
}
async function healthCheck(clientOrUrl, expectedChainIdOrOpts, rpcUrl) {
  let client;
  let expectedChainId;
  let opts = {};
  let resolvedRpcUrl = rpcUrl;
  if (typeof clientOrUrl === "string") {
    resolvedRpcUrl = clientOrUrl;
    const viemClient = createPublicClient2({ transport: http2(clientOrUrl) });
    client = viemClient;
    if (typeof expectedChainIdOrOpts === "object") {
      opts = expectedChainIdOrOpts;
      expectedChainId = opts.expectedChainId;
    } else {
      expectedChainId = expectedChainIdOrOpts;
    }
  } else {
    client = clientOrUrl;
    if (typeof expectedChainIdOrOpts === "object") {
      opts = expectedChainIdOrOpts;
      expectedChainId = opts.expectedChainId;
    } else {
      expectedChainId = expectedChainIdOrOpts;
    }
  }
  const start = Date.now();
  const checks = [];
  checks.push(
    await runCheck("rpc", async () => {
      const chainId = await client.request({
        method: "eth_chainId",
        params: []
      });
      return `Connected, chainId: ${String(parseInt(chainId, 16))}`;
    })
  );
  checks.push(
    await runCheck("chain", async () => {
      const chainId = await client.request({
        method: "eth_chainId",
        params: []
      });
      const actual = parseInt(chainId, 16);
      if (expectedChainId !== void 0 && actual !== expectedChainId) {
        throw new Error(
          `Expected chain ${String(expectedChainId)}, got ${String(actual)}`
        );
      }
      return `Chain ID: ${String(actual)}`;
    })
  );
  checks.push(
    await runCheck("blocks", async () => {
      const blockHex = await client.request({
        method: "eth_blockNumber",
        params: []
      });
      const blockBefore = parseInt(blockHex, 16);
      let miningVerified = false;
      try {
        await client.request({
          method: "evm_mine",
          params: []
        });
        const afterHex = await client.request({
          method: "eth_blockNumber",
          params: []
        });
        const blockAfter = parseInt(afterHex, 16);
        miningVerified = blockAfter > blockBefore;
      } catch {
      }
      const suffix = miningVerified ? " (mining: active)" : "";
      return `Block number: ${String(blockBefore)}${suffix}`;
    })
  );
  checks.push(
    await runCheck("evm", async () => {
      await client.request({
        method: "eth_call",
        params: [
          {
            to: "0x0000000000000000000000000000000000000000",
            data: "0x"
          },
          "latest"
        ]
      });
      let transientSupport = false;
      try {
        await client.request({
          method: "eth_call",
          params: [
            {
              data: TRANSIENT_STORAGE_PROBE_BYTECODE
            },
            "latest"
          ]
        });
        transientSupport = true;
      } catch {
      }
      const suffix = transientSupport ? " (transient storage: supported)" : " (transient storage: not available)";
      return `EVM execution working${suffix}`;
    })
  );
  checks.push(
    await runCheck("contracts", async () => {
      if (opts.expectedContracts && opts.expectedContracts.length > 0) {
        const missing = [];
        for (const addr of opts.expectedContracts) {
          const code2 = await client.request({
            method: "eth_getCode",
            params: [addr, "latest"]
          });
          if (!code2 || code2 === "0x") {
            missing.push(addr);
          }
        }
        if (missing.length > 0) {
          throw new Error(`Missing bytecode at: ${missing.join(", ")}`);
        }
        return `All ${String(opts.expectedContracts.length)} expected contracts verified`;
      }
      const code = await client.request({
        method: "eth_getCode",
        params: ["0x0000000000000000000000000000000000000001", "latest"]
      });
      return `Contract query working (precompile code: ${code.length > 2 ? "present" : "empty"})`;
    })
  );
  checks.push(
    await runCheck("funding", async () => {
      const minBal = opts.minBalance ?? 1000000000000000000n;
      if (opts.expectedFundedAccounts && opts.expectedFundedAccounts.length > 0) {
        const underfunded = [];
        for (const addr of opts.expectedFundedAccounts) {
          const balance2 = await client.request({
            method: "eth_getBalance",
            params: [addr, "latest"]
          });
          if (BigInt(balance2) < minBal) {
            underfunded.push(addr);
          }
        }
        if (underfunded.length > 0) {
          throw new Error(`Underfunded accounts: ${underfunded.join(", ")}`);
        }
        return `All ${String(opts.expectedFundedAccounts.length)} accounts funded`;
      }
      const balance = await client.request({
        method: "eth_getBalance",
        params: [ANVIL_DEPLOYER_ADDRESS, "latest"]
      });
      const balanceEth = Number(BigInt(balance)) / 1e18;
      if (balanceEth === 0) throw new Error("Deployer account has 0 ETH");
      return `Deployer balance: ${balanceEth.toFixed(2)} ETH`;
    })
  );
  const totalDurationMs = Date.now() - start;
  const healthy = checks.every((c) => c.passed);
  const hasWarnings = checks.some((c) => c.status === "warn");
  const status = healthy ? hasWarnings ? "degraded" : "healthy" : "unhealthy";
  const now = Date.now();
  return {
    healthy,
    status,
    checks,
    totalDurationMs,
    latencyMs: totalDurationMs,
    timestamp: now,
    timestampIso: new Date(now).toISOString(),
    rpcUrl: resolvedRpcUrl
  };
}
async function assertHealthy(client, expectedChainId) {
  const report = await healthCheck(client, expectedChainId);
  if (!report.healthy) {
    const failed = report.checks.filter((c) => !c.passed).map((c) => `${c.name}: ${c.message}`);
    throw new HealthCheckError(`Health check failed:
${failed.join("\n")}`);
  }
  return report;
}

// src/recorder.ts
import { readFileSync as readFileSync2, writeFileSync as writeFileSync2 } from "fs";
var TransactionRecorder = class {
  #recording = false;
  #transactions = [];
  #sessionId;
  #sessionName;
  #startedAt;
  #pollTimer;
  #lastBlockNumber;
  /** Start recording */
  start(opts) {
    this.#recording = true;
    this.#sessionId = opts?.sessionId ?? crypto.randomUUID();
    this.#sessionName = opts?.sessionName;
    this.#startedAt = Date.now();
    if (opts?.client) {
      const client = opts.client;
      const interval = opts.pollIntervalMs ?? 1e3;
      this.#lastBlockNumber = void 0;
      this.#pollTimer = setInterval(() => {
        void this.#pollBlocks(client);
      }, interval);
    }
  }
  /**
   * Stop recording.
   * Optionally accepts a name and description for the session.
   * Returns a RecordedSession snapshot.
   */
  stop(name, description) {
    this.#recording = false;
    if (this.#pollTimer !== void 0) {
      clearInterval(this.#pollTimer);
      this.#pollTimer = void 0;
    }
    const session = {
      id: this.#sessionId ?? crypto.randomUUID(),
      name: name ?? this.#sessionName,
      description,
      startedAt: this.#startedAt ?? Date.now(),
      endedAt: Date.now(),
      transactions: [...this.#transactions]
    };
    return session;
  }
  /** Whether currently recording */
  get recording() {
    return this.#recording;
  }
  /** Number of recorded transactions */
  get length() {
    return this.#transactions.length;
  }
  /** Record a transaction */
  record(tx) {
    if (!this.#recording) return;
    this.#transactions.push(tx);
  }
  /** Get all recorded transactions */
  getAll() {
    return [...this.#transactions];
  }
  /** Clear all recordings */
  clear() {
    this.#transactions = [];
    this.#sessionId = void 0;
    this.#sessionName = void 0;
    this.#startedAt = void 0;
  }
  /** Serialize recordings to JSON string (handles BigInt) */
  save() {
    return JSON.stringify(this.#transactions, bigintReplacer, 2);
  }
  /** Save as a full session with metadata. */
  saveSession(opts) {
    const session = {
      id: this.#sessionId ?? crypto.randomUUID(),
      name: this.#sessionName,
      startedAt: this.#startedAt ?? Date.now(),
      endedAt: Date.now(),
      chainId: opts?.chainId,
      transactions: this.#transactions
    };
    return JSON.stringify(session, bigintReplacer, 2);
  }
  /** Load recordings from JSON string */
  load(json) {
    this.#transactions = JSON.parse(
      json,
      bigintReviver
    );
  }
  /** Load a full session from JSON string. */
  loadSession(json) {
    const session = JSON.parse(json, bigintReviver);
    this.#transactions = session.transactions;
    this.#sessionId = session.id;
    this.#sessionName = session.name;
    this.#startedAt = session.startedAt;
    return session;
  }
  /**
   * Save a session to a file.
   * Uses BigInt-safe JSON serialization.
   */
  saveToFile(session, filePath) {
    writeFileSync2(
      filePath,
      JSON.stringify(session, bigintReplacer, 2),
      "utf-8"
    );
  }
  /**
   * Load a session from a file.
   * Uses BigInt-safe JSON deserialization.
   */
  static loadFromFile(filePath) {
    const raw = readFileSync2(filePath, "utf-8");
    return JSON.parse(raw, bigintReviver);
  }
  async replay(client, sessionOrUndefined, opts) {
    if (sessionOrUndefined) {
      const txs = sessionOrUndefined.transactions;
      const stats = { success: 0, failed: 0, skipped: 0 };
      for (let i = 0; i < txs.length; i++) {
        const tx = txs[i];
        opts?.onTx?.(tx, i);
        try {
          await client.request({ method: tx.method, params: tx.params });
          stats.success++;
        } catch {
          if (opts?.skipFailures) {
            stats.skipped++;
          } else {
            stats.failed++;
            return stats;
          }
        }
      }
      return stats;
    }
    for (const tx of this.#transactions) {
      await client.request({ method: tx.method, params: tx.params });
    }
  }
  async #pollBlocks(client) {
    if (!this.#recording) return;
    try {
      const blockHex = await client.request({
        method: "eth_blockNumber",
        params: []
      });
      const blockNum = BigInt(blockHex);
      if (this.#lastBlockNumber !== void 0 && blockNum > this.#lastBlockNumber) {
        for (let b = this.#lastBlockNumber + 1n; b <= blockNum; b++) {
          const hexBlock = `0x${b.toString(16)}`;
          try {
            const block = await client.request({
              method: "eth_getBlockByNumber",
              params: [hexBlock, true]
            });
            if (block?.transactions) {
              for (const rawTx of block.transactions) {
                const txObj = rawTx;
                this.#transactions.push({
                  method: "eth_sendTransaction",
                  params: [txObj],
                  result: txObj.hash ?? null,
                  timestamp: Date.now()
                });
              }
            }
          } catch {
          }
        }
      }
      this.#lastBlockNumber = blockNum;
    } catch {
    }
  }
};

export {
  TestnetError,
  AnvilStartupError,
  AnvilTimeoutError,
  PortConflictError,
  SnapshotNotFoundError,
  AccountError,
  DeploymentError,
  HealthCheckError,
  ANVIL_MNEMONIC,
  ANVIL_DEPLOYER_ADDRESS,
  ANVIL_ACCOUNTS,
  getAccount,
  generateAccounts,
  fundAccount,
  drainAccount,
  derivationPath,
  getAnvilVersion,
  AnvilManager,
  parseDuration,
  timeTravel,
  setTimestamp,
  setNextBlockTimestamp,
  mineBlocks,
  getBlockTimestamp,
  MOCK_ERC20_ABI,
  MOCK_ERC20_BYTECODE,
  sendTransactionSync,
  getERC20Balance,
  deployERC20,
  mintERC20,
  setERC20Balance,
  addEthBalance,
  setERC20Allowance,
  bigintReplacer,
  bigintReviver,
  stringifyWithBigInt,
  parseWithBigInt,
  DEFAULT_SNAPSHOT_PATH,
  SnapshotStore,
  healthCheck,
  assertHealthy,
  TransactionRecorder
};
//# sourceMappingURL=chunk-IHAME5K4.js.map